"use client";

import { useCallback, useEffect, useState } from "react";

// Журнал ошибок (07.09; серверные добавлены 21.09).
//
// Две стороны в одном списке:
//  · ФРОНТ — что упало у клиентов на телефонах и десктопах. Пишет
//    reportClientError: window.onerror, error boundary, падение чанка;
//  · СЕРВЕР — что сорвалось у нас. Пишет logServerError; первым источником
//    стали обращения к модели (заказ Никиты 21.09: «до сервера мне не
//    достучаться, пусть ошибки падают в зону оператора»).
//
// Серверные строки дублируются оператору в Telegram, кроме «пережито
// повтором»: те копятся молча и смотрятся глазами.

type Row = {
  id: string;
  tenantSlug: string | null;
  tenantName: string | null;
  userId: string | null;
  kind: string;
  message: string;
  stack: string | null;
  url: string | null;
  build: string | null;
  userAgent: string | null;
  extra: Record<string, unknown> | null;
  count: number;
  firstAt: string;
  lastAt: string;
};

type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

async function opApi<T = unknown>(key: string, action: string, payload?: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`/api?action=${encodeURIComponent(action)}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-key": key },
      body: JSON.stringify({ action, payload }),
    });
    return (await res.json()) as ApiResult<T>;
  } catch {
    return { ok: false, error: "Нет связи с сервером" };
  }
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const KIND_LABEL: Record<string, string> = {
  error: "ошибка",
  unhandledrejection: "промис",
  render: "рендер",
  chunk: "чанк после деплоя",
  ai: "модель · сбой",
  ai_retry: "модель · пережито повтором",
  api: "сервер · 500",
  telegram: "Telegram поддержки не ушёл",
};

/**
 * Пережитый повтором сбой — не красный.
 *
 * Он важен (болезнь видна до того, как дойдёт до людей), но человека не
 * зовут: если красить его как настоящую аварию, красным станет весь список и
 * читать его перестанут.
 */
const SURVIVED = new Set(["ai_retry"]);

/** Виды, которые пишет сервер, а не браузер клиента. */
const SERVER_KINDS = new Set(["ai", "ai_retry", "api", "telegram"]);

export default function ErrorsView({ apiKey }: { apiKey: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState("");
  const [slug, setSlug] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await opApi<{ errors: Row[] }>(apiKey, "opListClientErrors", { limit: 100, tenantSlug: slug.trim() || undefined });
    if (!r.ok) return setErr(r.error);
    setErr("");
    setRows(r.data.errors);
  }, [apiKey, slug]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Ошибки</h2>
        <span className="text-xs opacity-60">фронт у клиентов и сервер у нас</span>
        <input
          className="rounded-xl border border-black/10 px-3 py-1.5 text-sm"
          placeholder="фильтр по slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />
        <button className="rounded-full border border-black/15 px-3 py-1 text-sm" onClick={() => void load()}>
          Обновить
        </button>
      </div>
      {err && <div className="text-sm text-red-600">{err}</div>}
      {rows === null ? (
        <div className="text-sm opacity-60">Загружаю…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm opacity-60">Ошибок нет — хороший знак.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-black/10 p-3">
              <button className="w-full text-left" onClick={() => setOpen(open === r.id ? null : r.id)}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span
                    className={
                      SURVIVED.has(r.kind)
                        ? "rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
                        : "rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800"
                    }
                  >
                    {KIND_LABEL[r.kind] ?? r.kind}
                  </span>
                  <span className="font-medium">
                    {r.tenantName ?? r.tenantSlug ?? (SERVER_KINDS.has(r.kind) ? "все заведения" : "поддомен не распознан")}
                  </span>
                  {r.url && <span className="opacity-60">{r.url}</span>}
                  {r.build && <span className="opacity-60">· сборка {r.build}</span>}
                  {r.count > 1 && <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs">×{r.count}</span>}
                  <span className="ml-auto opacity-60">{fmt(r.lastAt)}</span>
                </div>
                <div className="mt-1 break-words font-mono text-[13px]">{r.message}</div>
              </button>
              {open === r.id && (
                <div className="mt-2 space-y-1 text-xs opacity-80">
                  <div>
                    первый раз {fmt(r.firstAt)}
                    {!SERVER_KINDS.has(r.kind) && <> · пользователь {r.userId ?? "—"}</>}
                  </div>
                  {r.userAgent && <div className="break-all">{r.userAgent}</div>}
                  {r.extra && Object.keys(r.extra).filter((k) => k !== "fp").length > 0 && (
                    <pre className="whitespace-pre-wrap break-all rounded-xl bg-black/5 p-2">
                      {JSON.stringify(Object.fromEntries(Object.entries(r.extra).filter(([k]) => k !== "fp")), null, 1)}
                    </pre>
                  )}
                  {r.stack && <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-black/5 p-2 font-mono">{r.stack}</pre>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
